// @ts-nocheck

/**
 * R90. `settings.bundle_product` reaches this file through a Liquid attribute,
 * and Liquid does not have a way to say "absent" in a string. An unset product
 * used to render as the four-character string "null" (the snippet piped the id
 * through `| json`), which is TRUTHY in JavaScript. The variant id rendered
 * empty, so the `!bundleProductId || !bundleProductAvailableVariantId` guard
 * still caught it and nothing appeared broken - but the reason it caught was
 * the OTHER argument. Any future root that supplies a variant id would have
 * posted the literal string "null" to Skio as a product platform id.
 *
 * That whole class of failure is why 75 subscriptions shipped with no box: the
 * misconfiguration was invisible because a stringified nothing looked like a
 * something. Normalise once, at the only place both values enter the app, and
 * treat every spelling of nothing as nothing.
 *
 * @param {unknown} value
 * @returns {string | null}
 */
function ctrNormaliseThemeSettingId(value) {
  if (value === null || value === undefined) return null;
  const trimmed = String(value).trim();
  if (trimmed === '' || trimmed === 'null' || trimmed === 'undefined') return null;
  return trimmed;
}

const CTR_DYNAMIC_BOX_TIMEOUT_MS = 10000;

// new-cart-drawer, 20 Sep 2026. The Subscribe & Save transformation beat.
//
// THE HOLD IS THE BRIEF'S, THE REVEAL IS MEASURED. 800ms sits in the middle of the
// 700-900ms the brief asks for. 900ms for the reveal is the row stagger (90ms x up
// to 6 rows = 540ms) plus the last row's own 320ms fade, rounded up - so the phase
// outlasts the animation it triggers rather than cutting it off.
// 3000. Raised from 800 to 2000 and then to 3000 on 20 Sep 2026, both times because Charlie
// read it and it was gone. Charlie: "let the confirmation at the end of the
// animation show for longer", and "the turn off copy is not showing" - which it was, for
// exactly 800ms. The off beat carries three lines and fifteen words; at an ordinary reading
// pace that is nearer three seconds than one, and the brief's original 700-900ms was written
// for "You just saved £3.30" alone.
const CTR_BEAT_SAVED_MS = 3000;
const CTR_BEAT_REVEAL_MS = 900;
const CTR_BEAT_FAILED_MS = 2600;

/**
 * The cart's OWN currency, not the shop's assumed one.
 *
 * The brief asks for this explicitly and formatMoney() below cannot do it: that one
 * hardcodes en-GB/GBP, which is right for today and silently wrong the day a second
 * market opens - it would print a euro total with a pound sign. `cart.currency` is an
 * ISO code in every /cart.js response, so the figure is formatted in the currency the
 * money was actually counted in.
 *
 * Intl throws RangeError on a currency code it does not recognise, which would take
 * the whole success beat down, so the fallback is a catch rather than a validation.
 *
 * @param {number} cents
 * @param {string} [currency] - ISO 4217, from cart.currency
 * @returns {string}
 */
function ctrFormatCartMoney(cents, currency) {
  const value = (Number(cents) || 0) / 100;
  const locale = document.documentElement.lang || 'en-GB';

  try {
    // NO minimumFractionDigits. Codex round SUBSAVE-TRANSFORM-2 finding 4: forcing two
    // made a JPY saving read "\u00a5350.00", a currency with no subunit. Intl already knows
    // each currency's own digits, and GBP still comes out as \u00a33.30. No reachable
    // surface today - this shop is single-market - so it is a correctness fix, not a bug fix.
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: currency || 'GBP',
    }).format(value);
  } catch (error) {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
    }).format(value);
  }
}

document.addEventListener("alpine:init", () => {
  Alpine.store("cartDrawerStore", {
    products: Alpine.$persist([]),
    // R57c-3: cartDrawerSectionId is gone. It was written once in init() and read
    // by nobody after R57b moved mutation discovery to the registry.
    sellingPlan: Alpine.$persist(null),
    dynamicBoxId: Alpine.$persist(null),
    // R90: per page load, deliberately NOT persisted - a misconfiguration that
    // is still present on the next page load deserves to be said again.
    _loggedMissingBundleProduct: false,
    dynamicBoxSelectableProductVariants: Alpine.$persist([]),
    isWholesaleCustomer: Alpine.$persist(false),
    isSubscriptionActive: Alpine.$persist(false),

    // new-cart-drawer, 20 Sep 2026. Whether the Subscribe & Save card is
    // expanded. It is a SEPARATE axis from isSubscriptionActive: the design
    // has an off/expanded state (the three selling points, no cadence) that
    // the old markup could not express, because "expanded" was `x-show` bound
    // straight to isSubscriptionActive.
    //
    // IT IS ON THE STORE AND NOT IN AN `x-data` ON THE CARD. Putting `x-data`
    // on that wrapper broke the live cart for four hours on 19 Sep 2026 -
    // `$root` stopped resolving to `.alpine-cart-drawer`, so the products
    // template was unreachable and every conversion pass threw. The full
    // account is in the comment above the wrapper in
    // snippets/ctr-cart-subscription.liquid. There is no second Alpine root
    // inside this drawer and there must never be one.
    //
    // DELIBERATELY NOT $persist, and it was $persist until Codex round
    // NEW-CART-DRAWER-2 finding 1. Persisting it was a nice-to-have - reopen the
    // drawer, find the benefits where you left them - and it quietly broke the
    // existing-subscriber case. The collapse in ctr-cart-subscription.liquid is a
    // Liquid-rendered CLASS, applied server-side on every render, and it hides both
    // detail panels with `!important`. A persisted `true` hydrates at Alpine init
    // WITHOUT either click handler running, so a customer tagged
    // `Active product subscription`, holding a one-time cart, who had opened the
    // benefits on an earlier visit, got `aria-expanded="true"` over a panel the class
    // was still hiding. The comment beside that binding claimed the class could only
    // survive while this flag was false "because both handlers clear it first" - true
    // of the handlers, and $persist is a third way in that bypasses both.
    // Per-page-load also matches the design, whose default state is collapsed.
    isSubscriptionPanelOpen: false,


    // R57c-5c: the customer's CURRENT conversion intent, held on the store
    // because two cartDrawer() roots exist on build-a-box - the lifted drawer
    // and the persistent basket - and they do not share instance state. The
    // first attempt at this guard kept the flags on `this`, and the smoke showed
    // exactly why that fails: the perma-cart root is the one whose radio the
    // customer clicks and it recorded intent correctly, while the LIFTED drawer
    // is the store owner and its reconcileSubscriptionState ran with its own
    // stale instance flags and re-subscribed the cart anyway. Same class as the
    // instance-local `_reconciling` that R57b-5 had to lift out.
    // Deliberately NOT $persist: intent is per-interaction, not per-session.
    // R57c-8, rebuilt to Codex's spec after four booleans accreted across seven
    // commits and produced three separate ordering bugs.
    //
    // desiredPlan is the ONE piece of intent:
    //   undefined -> no explicit choice, derive the target from the cart
    //   null      -> the customer explicitly chose one-time
    //   <plan id> -> the customer explicitly chose that subscription
    // conversionGeneration is bumped by every new intent, so a worker can tell
    // that it has been superseded instead of racing a second worker.
    // conversionRunning is the single worker mutex, held on the STORE because two
    // cartDrawer() roots exist and instance state is not shared between them.
    desiredPlan: undefined,
    conversionGeneration: 0,
    conversionRunning: false,
    // Codex round 6: a mismatch spotted while the worker is busy must not be
    // dropped on the floor by the mutex. It is remembered and drained instead.
    conversionPending: false,

    // new-cart-drawer, 20 Sep 2026. THE TRANSFORMATION BEAT, and it is a SEPARATE
    // axis from `conversionRunning`.
    //
    // conversionRunning is the worker's mutex: true the instant the worker takes it,
    // false the instant it lets go. That is exactly the wrong lifetime for what the
    // customer sees, because the success beat and the staggered reveal both happen
    // AFTER the cart work has finished. Binding the overlay to the mutex, as the
    // first version of this did, means the cover vanishes the moment the last line
    // settles and there is nowhere to say what was saved.
    //
    // Phases: idle -> loading -> saved -> revealing -> idle, or loading -> failed -> idle.
    // `saved` is skipped when the measured saving is not positive - see
    // finishConversionBeat. Deliberately NOT $persist: a half-finished animation
    // rehydrating on the next page load is a bug, not a feature.
    conversionPhase: 'idle',
    // Which outcome the beat in flight is about: 'on' subscribing, 'off' back to one-time,
    // 'frequency' a cadence change. Only 'on' ever names a figure.
    conversionDirection: 'on',
    // Bumped per BEAT, not per intent - conversionGeneration already does per-intent.
    // Every timer below carries the token it was armed under and retires itself if a
    // newer beat has started, which is what stops a stale timer driving the phase of
    // an animation nobody is watching any more.
    conversionBeat: 0,
    /** @type {Array<ReturnType<typeof setTimeout>>} */
    _conversionTimers: [],

    // THE SAVING IS MEASURED, NEVER CALCULATED.
    //
    // `conversionBaseline` is cart.total_price as the SERVER reported it on the first
    // planner call of this beat - the cart the customer tapped over. The saving is the
    // difference against the server's total once the conversion has settled.
    //
    // The previous overlay summed 10% of every line whose product had a selling plan,
    // in Liquid. That is right for most baskets and wrong for any basket Shopify treats
    // differently - a line-level discount code, a plan whose discount is not 10%, a
    // plan with a fixed amount off, rounding on a line ending in a half penny. A
    // before/after read cannot disagree with the total the customer is about to pay,
    // because it IS that total.
    conversionBaseline: null,
    conversionSaving: 0,
    conversionSavingText: '',
    conversionCurrency: 'GBP',

    // THE FAILURE LATCH HAS TO OUTLIVE THE WORKER, and it did not.
    //
    // Codex round SUBSAVE-TRANSFORM finding 1, reproduced against the real store: the
    // worker's `notified` flag is a local, and on a Shopify refusal `changeLines()`
    // reconciles before returning, which re-enters updateCartItems() and raises
    // `conversionPending`. The `finally` then hands the beat to a FRESH worker with a
    // FRESH latch. That second worker finds a cart Shopify never changed, plans no work,
    // reports ok, and closed the beat with "Your basket has been updated." - after a
    // refusal. The banner said one thing and the live region said the opposite.
    //
    // A beat is one customer intent, so the latch belongs to the beat. Cleared only when
    // a genuinely new beat is minted, never by a worker joining one already running.
    conversionBeatFailed: false,

    // THE LOCKS ANSWER TO THE CONVERSION, THE ANIMATION ANSWERS TO THE PHASE. Codex round
    // CART-DAY2 findings 2 and 3, both P1 and both the same mistake: the checkout guard, the
    // discount guard and the row `inert` were all bound to `conversionPhase === 'loading'`.
    //
    // That is the ANIMATION's state, and there are two ways a cart is mid-conversion without
    // it:
    //   - the dynamic-box readiness gate returns BEFORE the mutex is taken, so for up to the
    //     10s dependency timeout the toggle reads subscribed while the cart is untouched and
    //     checkout will happily submit it;
    //   - a reconcile-driven conversion runs deliberately SILENTLY (Charlie, 20 Sep), so the
    //     phase never leaves `idle` while the worker rewrites every line.
    // In both, `cart-items-disabled` is `pointer-events: none` and nothing else, so a focused
    // checkout button still submits on Enter. That is a mixed basket reaching Shopify, which
    // is the one outcome the whole conversion worker exists to prevent.
    get conversionBusy() {
      return (
        this.conversionRunning ||
        this.conversionPending ||
        this.dynamicBoxResumeGeneration !== null
      );
    },

    // The cart total the displayed saving was measured against. Codex round CART-DAY2
    // finding 4: clearing it only while the phase is `idle` leaves it stale for any cart
    // change that lands during `saved` or `revealing` - and the discount control is operable
    // throughout both. Anchoring to the total makes the test independent of the animation.
    conversionMeasuredTotal: null,

    // THE ANIMATION IS FOR A TAP, NOT FOR HOUSEKEEPING. Charlie, 20 Sep 2026: "i am
    // getting the animatio still without toggling sub and save".
    //
    // The conversion worker runs for two quite different reasons and only one of them is
    // a transformation the customer asked to watch:
    //   1. They flipped Subscribe & Save or changed the cadence - their basket is about
    //      to change under them and the cover is the point.
    //   2. `reconcileSubscriptionState()` is keeping an already-subscribed cart
    //      consistent - most often because they ADDED an item, which then has to adopt
    //      the cart's existing plan. Nothing is transforming from where they are sitting;
    //      they added a drink. Covering the basket and saying "Giving your box the VIP
    //      treatment" there is a lie about what just happened.
    //
    // `desiredPlan !== undefined` is close but not the signal: reorderSellingPlans() also
    // records an intent, and that is configuration arriving from the PDP, which can fire
    // on load. So the two customer-facing handlers raise this explicitly and the worker
    // consumes it when it takes the mutex. Case 2 runs exactly as it did before any of
    // this existed - silently.
    transformationRequested: false,

    // WHICH OUTCOME THE BEAT IS ABOUT: 'on', 'off' or 'frequency'. Charlie, 20 Sep 2026,
    // asked for one universal processing state and three different success messages, and a
    // frequency change is the one the on/off axis cannot express - it KEEPS a selling plan,
    // so it derives as 'on' and would have claimed a saving that did not change. The handler
    // that knows what the customer did records it here rather than the worker guessing from
    // the cart afterwards.
    transformationKind: null,

    /**
     * The ONE polite announcement, assembled from copy the Liquid put on the element
     * so the strings stay in locales/ and out of this file.
     *
     * `revealing` gets the neutral done line rather than the saving, because that
     * phase is also where an "off" conversion lands and a customer switching BACK to
     * one-time must not be told they saved anything.
     *
     * @param {DOMStringMap} copy - the live region's own data-* attributes
     * @returns {string}
     */
    conversionAnnouncement(copy) {
      if (!copy) return '';

      switch (this.conversionPhase) {
        case 'loading':
          // FINDING 4 OF THE FINAL ROUND, P2. This read `direction === 'on' ? on : off`, and
          // 'frequency' is a third direction now, so every cadence change announced "Updating
          // your basket to a one-off purchase." - contradicting both what the customer did and
          // the neutral processing copy they could see. All three directions are named.
          if (this.conversionDirection === 'off') return copy.loadingOff || '';
          if (this.conversionDirection === 'frequency') return copy.loadingFrequency || '';
          return copy.loadingOn || '';
        case 'saved':
          // Only the 'on' outcome names a figure; the other two describe what changed.
          if (this.conversionDirection === 'off') return copy.leaving || '';
          if (this.conversionDirection === 'frequency') return copy.frequency || '';
          // The on outcome with nothing to name gets its own line rather than a £0.00.
          if (this.conversionSaving <= 0) return copy.onNofigure || '';
          return (copy.saved || '').replace('[amount]', this.conversionSavingText);
        case 'revealing':
          return copy.done || '';
        case 'failed':
          // NOTHING. Codex round SUBSAVE-TRANSFORM finding 6: the worker already routes
          // every failure through `#cart-error-live` (assets/cart-controller.js), which is
          // assertive and carries Shopify's own wording. Announcing here as well gave one
          // failure two live regions and two different sentences. The visual beat stays -
          // it is aria-hidden, so it is decoration over an announcement that already
          // happened - and `data-failed` is kept on the element as the copy's home.
          return '';
        default:
          return '';
      }
    },

    /**
     * Close whatever beat is open. Safe to call when none is.
     *
     * Codex round SUBSAVE-TRANSFORM-2 finding 1, which is the bug Charlie hit: the
     * `conversionPending` drain deliberately does NOT close the beat, because the next
     * worker is going to continue it - but `updateCartItems()` has four early returns
     * ABOVE the line that opens one, and any of them turns that hand-off into a beat
     * nobody owns. The cover then sits over a settled cart with checkout disabled until
     * the page is reloaded, and because the store is shared it takes Build a Box's
     * checkout with it.
     *
     * @param {boolean} announce - true where the customer has already been told something
     *   went wrong, so the failure beat is the matching visual; false where nothing went
     *   wrong for them (a superseded intent, a resume that will open its own beat).
     */
    abortConversionBeat(announce) {
      if (this.conversionPhase === 'idle') return;

      if (announce) {
        this.conversionBeatFailed = true;
        this.finishConversionBeat(this.conversionBeat, false, null);
        return;
      }

      this.clearConversionTimers();
      this.conversionPhase = 'idle';
    },

    /**
     * THE BACKSTOP, and it exists because I could not enumerate the paths.
     *
     * The four returns above are now closed explicitly, but this class of bug has bitten
     * twice in one day and the honest response to that is a net rather than a longer list
     * of fixed cases. The condition is tight rather than a timeout: a beat can only be
     * driven forward by a worker, `conversionRunning` says whether one is holding it, and
     * the gap between a worker's `finally` and a drained worker starting is a single
     * task. So `loading` with no worker for a continuous 1.5s means nothing is coming.
     *
     * Silent: whatever went wrong has already said its piece through the error banner.
     */
    armConversionWatchdog(token) {
      let idleTicks = 0;

      const tick = () => {
        if (this.conversionBeat !== token) return;
        if (this.conversionPhase !== 'loading') return;

        idleTicks = this.conversionRunning ? 0 : idleTicks + 1;

        if (idleTicks >= 3) {
          console.error('[cart-drawer-alp] conversion beat was left open with no worker holding it; closing the cover');
          this.abortConversionBeat(false);
          return;
        }

        this._conversionTimers.push(setTimeout(tick, 500));
      };

      this._conversionTimers.push(setTimeout(tick, 500));
    },

    clearConversionTimers() {
      this._conversionTimers.forEach((timer) => clearTimeout(timer));
      this._conversionTimers = [];
    },

    /**
     * Opens a beat, or JOINS the one already running.
     *
     * A worker that re-enters while the phase is still `loading` is the pending drain
     * or a new intent landing mid-conversion, and it keeps the SAME baseline on
     * purpose: the figure shown to a customer has to be measured from the cart they
     * tapped over, not from whatever the previous pass happened to leave behind.
     *
     * @param {'on' | 'off'} direction
     * @returns {number} the beat token to pass back to finishConversionBeat
     */
    beginConversionBeat(direction) {
      this.clearConversionTimers();

      if (this.conversionPhase !== 'loading') {
        this.conversionBeat += 1;
        this.conversionBaseline = null;
        this.conversionSaving = 0;
        this.conversionSavingText = '';
        this.conversionBeatFailed = false;
      }

      this.conversionDirection = direction;
      this.conversionPhase = 'loading';
      this.armConversionWatchdog(this.conversionBeat);

      return this.conversionBeat;
    },

    /**
     * @param {number} token - the value beginConversionBeat returned
     * @param {boolean} ok - false when the worker surfaced a failure to the customer
     * @param {number | null} afterTotal - cart.total_price from the settled cart
     */
    finishConversionBeat(token, ok, afterTotal) {
      if (this.conversionBeat !== token) return;

      this.clearConversionTimers();

      // `ok` is what THIS worker knows; `conversionBeatFailed` is what the whole beat
      // knows. A beat that failed at any point cannot be closed as a success by a later
      // worker that happened to find nothing left to do.
      if (this.conversionBeatFailed) ok = false;

      /**
       * @param {() => void} fn
       * @param {number} ms
       */
      const later = (fn, ms) => {
        this._conversionTimers.push(
          setTimeout(() => {
            if (this.conversionBeat !== token) return;
            fn();
          }, ms)
        );
      };

      const toIdle = () => later(() => { this.conversionPhase = 'idle'; }, CTR_BEAT_REVEAL_MS);

      if (!ok) {
        this.conversionPhase = 'failed';
        later(() => { this.conversionPhase = 'idle'; }, CTR_BEAT_FAILED_MS);
        return;
      }

      const before = this.conversionBaseline;
      const saving =
        before == null || afterTotal == null || isNaN(afterTotal)
          ? 0
          : before - afterTotal;

      this.conversionSaving = saving > 0 ? saving : 0;
      this.conversionSavingText = ctrFormatCartMoney(this.conversionSaving, this.conversionCurrency);
      this.conversionMeasuredTotal = afterTotal == null || isNaN(afterTotal) ? null : afterTotal;

      // NO CELEBRATION WITHOUT A SAVING - but SWITCHING OFF IS NOT A MISSING CELEBRATION,
      // it is a different one. Charlie, 20 Sep 2026: "the closing animation makes no sense".
      // It was skipping straight to the reveal because the rule below was written as "no
      // saving, no beat", and switching off has no saving. True, and it left the one
      // direction where the basket gets MORE expensive with nothing said about it. The off
      // direction now gets its own beat, which names the change rather than celebrating it;
      // the copy is in locales under cart_transform_leaving_*.
      //
      // The rule still holds for the direction it was written for: subscribing with nothing
      // subscribable in the basket, or a beat rejoined after a toggle back and forth that
      // ended where it started, would say "You just saved £0.00", which is worse than
      // saying nothing.
      // CONFIRMATION ROUND FINDING 2. This used to skip the beat entirely when subscribing
      // produced no figure, which left that outcome with the neutral done line and gave the
      // surface two distinct confirmations rather than three. The rule it was protecting is
      // still right - "You just saved £0.00" is worse than silence - so the answer is a second
      // ON confirmation with no number in it rather than no confirmation. Charlie's wording,
      // 20 Sep 2026: "same price". The block is selected by `data-conversion-figure`.
      this.conversionPhase = 'saved';
      later(() => {
        this.conversionPhase = 'revealing';
        toIdle();
      }, CTR_BEAT_SAVED_MS);
    },
    // R49a: the in-flight Skio dynamic-box request, held on the STORE for the
    // same reason conversionRunning is - build-a-box renders TWO cartDrawer()
    // roots (snippets/ctr-cart-drawer.liquid:86 and snippets/ctr-perma-cart.liquid:58)
    // and they do not share instance state. The persisted result fields cannot
    // do this job: they are read synchronously and written asynchronously, so on
    // a cold load both roots passed the "already populated" check before either
    // response landed, and the page made two identical requests.
    // Deliberately NOT $persist: a promise does not survive a page load, and a
    // persisted one would short-circuit the next load with a dead value.
    dynamicBoxRequest: null,
    // R84, Codex round 33 finding 3: at most ONE store-level continuation is
    // registered against the in-flight request, keyed by the conversion
    // generation it was registered for. A second toggle while one is armed
    // bumps the generation, so the armed continuation retires itself instead of
    // converting against an intent nobody holds any more.
    dynamicBoxResumeGeneration: null,
    // R84b, Codex round 34 finding 1. Readiness cannot be inferred from the
    // presence of the settled promise, because the promise is KEPT on success.
    // A validated response with an EMPTY selectable list is ready and has a
    // boxId; it is not pending, and reading it as pending made the resume
    // continuation re-enter an already-settled promise for ever.
    dynamicBoxFetched: false,
    // Current-page permission for subscribed cart adds. Persisted result fields
    // are independent, so neither one alone proves that usable box data exists.
    // Deliberately NOT $persist: each page must validate configuration and any
    // request it starts before the central add boundary may use the box ID.
    dynamicBoxAddReady: false,
    /**
     * @param {string | number | null} sellingPlanId
     */
    setSellingPlan(sellingPlanId) {
      this.sellingPlan = Number(sellingPlanId) || null;
    },
    setIsWholesaleCustomer(isWholesaleCustomer) {
      this.isWholesaleCustomer = isWholesaleCustomer;

      if (isWholesaleCustomer) {
        this.sellingPlan = null;
        this.dynamicBoxId = null;
        this.dynamicBoxSelectableProductVariants = [];
        this.dynamicBoxAddReady = false;
      }
    },
    setDynamicBoxId(dynamicBoxId) {
      this.dynamicBoxId = dynamicBoxId;
    },
    setDynamicBoxSelectableProductVariants(dynamicBoxSelectableProductVariants) {
      this.dynamicBoxSelectableProductVariants = dynamicBoxSelectableProductVariants;
    },
    /**
     * R49a, Codex round 25 finding 2. At most ONE api.skio.com request per page
     * life, where a cold build-a-box load previously made two.
     *
     * This is a de-duplication repair and nothing else. The request still leaves
     * at the same point in init() as before, because round 25 finding 1 parked
     * timing deferral: the "99 KB on every page" premise is contradicted by the
     * persisted guard below, and no candidate late trigger covers the conversion
     * worker, which reaches planLineChanges from init() with no user in the path.
     *
     * It returns a promise so the central subscribed-add boundary can await the
     * current request. Cart-conversion planning keeps its established continuation
     * flow rather than blocking init() on this request.
     *
     * @param {string | null} bundleProductId
     * @param {string | null} bundleProductAvailableVariantId
     * @returns {Promise<void>}
     */
    ensureDynamicBox(bundleProductId, bundleProductAvailableVariantId) {
      if (!bundleProductId || !bundleProductAvailableVariantId) {
        this.dynamicBoxAddReady = false;
        // R90: say it out loud, once per page. This branch is not a normal
        // state - `settings.bundle_product` is a single global theme setting,
        // so if it is missing here it is missing on every page for every
        // customer, and every subscription add on the site is either blocked
        // (dev guards present) or boxless (live, no guards). The previous
        // silence is precisely why the misconfiguration ran unnoticed: the
        // symptom surfaced three files away as "dynamic-box readiness is
        // unavailable", which reads like a transient fetch problem and sent
        // three separate investigations looking at the wrong code.
        if (!this._loggedMissingBundleProduct) {
          this._loggedMissingBundleProduct = true;
          console.error(
            '[cart-drawer] Theme setting `bundle_product` is not resolving to a product. ' +
              'Subscriptions cannot carry box properties until it does. ' +
              'Check Customize -> Theme settings -> Bundle product, and check the chosen ' +
              'product is ACTIVE and published to the Online Store sales channel - an ' +
              'unpublished product makes `settings.bundle_product` render as nothing.',
            { bundleProductId, bundleProductAvailableVariantId }
          );
        }
        return Promise.resolve({ ready: false, reason: 'not-applicable' });
      }

      // A wholesale customer has just had both result fields cleared by
      // setIsWholesaleCustomer(), which init() calls before it gets here, and
      // resolveTargetPlan() returns null for them so no line of theirs can ever
      // take the box properties. The old code fetched anyway and the response
      // re-populated exactly the fields that clear exists to empty.
      if (this.isWholesaleCustomer) {
        this.dynamicBoxAddReady = false;
        return Promise.resolve({ ready: false, reason: 'not-applicable' });
      }

      // Both independently persisted fields must be complete before they can
      // authorize adds on a new page. A lone stale ID must fall through to a
      // current request and remain unavailable unless that request validates.
      if (this.dynamicBoxId && this.dynamicBoxSelectableProductVariants?.length > 0) {
        this.dynamicBoxAddReady = true;
        return Promise.resolve({ ready: true, reason: 'persisted' });
      }

      this.dynamicBoxAddReady = false;

      // The single flight. There is no await point between this check and the
      // assignment below, so the second root cannot land in the window.
      if (this.dynamicBoxRequest) return this.dynamicBoxRequest;

      const abortController = new AbortController();
      const timeout = window.setTimeout(
        () => abortController.abort(),
        CTR_DYNAMIC_BOX_TIMEOUT_MS
      );

      this.dynamicBoxRequest = fetch('https://api.skio.com/storefront-http/get-dynamic-box', {
        method: 'POST',
        body: JSON.stringify({ productPlatformId: bundleProductId }),
        signal: abortController.signal,
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        }
      })
        .then((response) => {
          // There was no response check at all. An HTTP error body still went
          // to .json(), and with no .catch anywhere the failure surfaced as an
          // unhandled rejection.
          if (!response.ok) {
            throw new Error(`get-dynamic-box responded ${response.status}`);
          }
          return response.json();
        })
        .then((payload) => {
          const dynamicBox = payload?.DynamicBox;
          const selectable = dynamicBox?.selectableProductVariants;

          if (!dynamicBox || !Array.isArray(selectable)) {
            throw new Error('get-dynamic-box returned no usable DynamicBox');
          }
          // A populated variants list with no box id is the one shape that must
          // not be written: it arms the persisted guard for the rest of the
          // storage lifetime while planLineChanges reads dynamicBoxId as falsey
          // and quietly plans every subscribed line without the properties.
          if (!dynamicBox.boxId) {
            throw new Error('get-dynamic-box returned a DynamicBox with no boxId');
          }

          const selectableProductVariants = selectable.flatMap((product) => {
            const productVariants = product?.productVariants;
            if (!Array.isArray(productVariants)) {
              throw new Error('get-dynamic-box returned a selectable product with no productVariants array');
            }
            return productVariants.map((variant) => {
              const id = Number(
                String(variant?.platformId ?? '').replace('gid://shopify/ProductVariant/', '')
              );
              // Partial parses are rejected rather than written: a half-filled
              // array would arm the guard above and never be retried.
              if (!Number.isInteger(id) || id <= 0) {
                throw new Error('get-dynamic-box returned a variant with no numeric platformId');
              }
              return id;
            });
          });

          // R90b, Codex round 35 finding 1 (P1). A VALIDATED EMPTY selectable
          // list is NOT a ready box, and round 33's reasoning that "planning
          // every line without box properties is CORRECT there" does not
          // survive contact with the rest of the system:
          //
          //   `withDynamicBox()` in cart-controller.js stamps `_dynamicBoxId`
          //   on EVERY add carrying an approved plan, and THROWS rather than
          //   send the add when the box is unavailable. A subscription line
          //   with no box id is therefore not a subscription this store can
          //   fulfil; it is the boxless line this release exists to stop.
          //
          // Arming readiness on an empty list let the precondition gate in
          // updateCartItems() pass, and planLineChanges() then applied the plan
          // while suppressing the properties - the live incident, rebuilt.
          //
          // The trade is stated rather than implied: FAIL CLOSED. A blocked
          // conversion costs one sale and the customer can retry. A boxless
          // recurring subscription bills forever and has to be migrated by hand,
          // which is the position this store is already in.
          //
          // The in-flight promise is cleared exactly as the failure branch below
          // clears it, so a later explicit ensureDynamicBox call or the next page
          // load may try again. Nothing schedules a retry by itself.
          if (selectableProductVariants.length === 0) {
            this.dynamicBoxRequest = null;
            this.dynamicBoxFetched = false;
            this.dynamicBoxAddReady = false;
            console.error(
              '[cart-drawer-alp] get-dynamic-box returned box ' +
                String(dynamicBox.boxId) +
                ' with an EMPTY selectableProductVariants list. No subscription line can be given ' +
                'box properties, so subscription conversion is blocked rather than allowed to create ' +
                'boxless subscriptions. Check the Skio dynamic box for product ' +
                String(bundleProductId) +
                ' actually has selectable products.'
            );
            return { ready: false, reason: 'empty-box' };
          }

          this.setDynamicBoxSelectableProductVariants(selectableProductVariants);
          this.setDynamicBoxId(dynamicBox.boxId);
          this.dynamicBoxFetched = true;
          this.dynamicBoxAddReady = true;

          // R84, Codex round 33 finding 2 still holds for every OTHER shape:
          // every path resolves to an explicit readiness result rather than
          // leaving a caller to infer one.
          return { ready: true, reason: 'fetched' };
        })
        .catch((error) => {
          // Retry semantics, stated rather than implied. ONLY the in-flight
          // promise is cleared, and only on failure:
          //   failure -> both persisted fields are untouched, the flight is
          //     released, and the next explicit ensureDynamicBox call in this
          //     page life, or the next page load, may try again. Nothing
          //     schedules a retry by itself and nothing loops.
          //   success -> the settled promise is KEPT, so a later call in the
          //     same page returns it instead of re-requesting even in the case
          //     where the response legitimately carried an empty variants list
          //     and the persisted guard never armed.
          this.dynamicBoxRequest = null;
          this.dynamicBoxAddReady = false;
          console.error(
            '[cart-drawer-alp] the Skio dynamic-box request failed; the subscription conversion will not run and the shopper intent is retained',
            error
          );

          return { ready: false, reason: 'failed' };
        })
        .finally(() => {
          window.clearTimeout(timeout);
        });

      return this.dynamicBoxRequest;
    },
    /**
     * R84. What the CURRENT state says, without starting anything.
     *   'ready'          - a validated response has landed, or a previous page
     *                      life persisted one, AND it names at least one
     *                      selectable variant. R90b: a validated EMPTY list is
     *                      NOT ready. It used to be, on the argument that Skio
     *                      had said nothing is box-eligible; every subscription
     *                      line still needs `_dynamicBoxId` (see
     *                      `withDynamicBox()` in cart-controller.js), so an
     *                      empty box means no subscription can be planned at
     *                      all, not that it can be planned without properties.
     *   'not-applicable' - a wholesale customer, whose lines can never take box
     *                      properties because resolveTargetPlan returns null
     *   'pending'        - a request is genuinely in flight
     *   'unavailable'    - never asked, the last ask failed, or the theme has no
     *                      bundle product configured
     *
     * ⚠️ Round 34 finding 2: MISSING CONFIGURATION IS 'unavailable', NOT
     * 'not-applicable'. `settings.bundle_product` feeds both cart-drawer roots,
     * so an unset value is a site-wide misconfiguration, not a per-page normal
     * state — and letting it through reproduced exactly the false convergence
     * this release exists to stop, for the "never fetched" case. Only one-time
     * (`target === null`) and wholesale were ever authorised to skip the gate.
     * @returns {'ready'|'not-applicable'|'pending'|'unavailable'}
     */
    dynamicBoxReadiness(bundleProductId, bundleProductAvailableVariantId) {
      if (this.isWholesaleCustomer) return 'not-applicable';
      if (!bundleProductId || !bundleProductAvailableVariantId) return 'unavailable';
      // Both conditions are required, in both branches. `dynamicBoxFetched` is
      // per page life; the variants list and the id are persisted across page
      // lives, so a stale storage entry must clear the same bar as a fresh
      // response rather than a lower one.
      if (this.dynamicBoxFetched && this.dynamicBoxSelectableProductVariants?.length > 0) return 'ready';
      if (this.dynamicBoxId && this.dynamicBoxSelectableProductVariants?.length > 0) return 'ready';
      if (this.dynamicBoxRequest) return 'pending';
      return 'unavailable';
    },
    updateProducts(products) {
      this.products = products;
    },
  });
  /**
   * @param {{sellingPlans: Array<{id: number | null, name: string}>, sectionId: string, bundleProductId: string, bundleProductAvailableVariantId: string}} component
   */
  Alpine.data(
    "cartDrawer",
    function ({
      sellingPlans = [],
      sectionId = "",
      bundleProductId = null,
      bundleProductAvailableVariantId = null,
      isWholesaleCustomer = false,
    }) {
      return {
        // Which line's "View selection" panel is open, by cart line key, or null. Component
        // state for the same reason as the pager: one surface, nothing else reads it.
        openSelection: null,
        // Which card the pager is showing. Component state, not store state: it is one
        // scroll position on one surface and nothing else reads it.
        cartTrackIndex: 0,
        sellingPlans: [...sellingPlans].filter(plan =>
          [2274525299, 2274558067, 2274590835, 2274623603].includes(Number(plan.id))
        ),
        // Removed benefitsActive since benefits are always shown when subscription is active
        sectionId,
        /** @type {Array<{id: number, quantity: number, sellingPlan: number | null}>} */
        cartProducts: Alpine.$persist([]),
        /** @type {Array<{id: number}>} */
        cartProductsWithoutAvailableSellingPlans: [],
        isWholesaleCustomer: isWholesaleCustomer === "true",
        // R90: normalised here, the one place both values enter the app, so
        // every downstream reader (init, planLineChanges, addToCart,
        // dynamicBoxReadiness) sees null rather than a string that spells it.
        bundleProductId: ctrNormaliseThemeSettingId(bundleProductId),
        bundleProductAvailableVariantId: ctrNormaliseThemeSettingId(
          bundleProductAvailableVariantId
        ),

        init() {
          // R57b-5 established the single store owner: two cartDrawer roots exist
          // on build-a-box (the lifted drawer plus the persistent basket), and
          // both used to register the global store listeners, so every
          // cart-drawer-store:update ran updateProducts and
          // reconcileSubscriptionState twice - and because _reconciling is
          // instance-local, both instances could independently launch the
          // clear+add rebuild. Per-instance UI state (selling-plan reordering)
          // stays per instance.
          //
          // R57c-3: the seat is held by the controller, which stores the owning
          // INSTANCE rather than a bare boolean, so a claim only succeeds when the
          // seat is genuinely empty and a release can only be made by the root
          // sitting in it. If the controller is missing, no root claims and the
          // store is left alone rather than risking two owners.
          if (!window.cartController?.claimStoreOwner) {
            console.error(
              '[cart-drawer-alp] cart-controller.js is not loaded; no root can own the cart store and the drawer will not track cart changes'
            );
          }
          this._isStoreOwner = !!window.cartController?.claimStoreOwner(this);

          this.$store.cartDrawerStore.setIsWholesaleCustomer(
            this.isWholesaleCustomer
          );

          if (this._isStoreOwner) {
            this.$store.cartDrawerStore.updateProducts(this.parseCartProductsJSON());

            // Reconcile the subscribe toggle with the real cart state on load.
            this.reconcileSubscriptionState();

            // Ensure a valid default frequency: covers fresh visitors (null) and
            // persisted selections that are no longer offered (e.g. 5/6 week plans)
            const storedPlan = this.$store.cartDrawerStore.sellingPlan;
            const storedPlanAvailable = storedPlan && this.sellingPlans.some(plan => plan.id === Number(storedPlan));
            if (this.$store.cartDrawerStore.isSubscriptionActive && !storedPlanAvailable) {
              this.$store.cartDrawerStore.setSellingPlan(this.getDefaultSellingPlan());
            }
          }

          // R49a: both roots reach the same store-level single flight, at the
          // SAME point in init() the per-instance fetch used to sit at. The
          // request is de-duplicated, not deferred, and it is deliberately not
          // awaited - nothing below it waits on the store being populated.
          this.$store.cartDrawerStore.ensureDynamicBox(
            this.bundleProductId,
            this.bundleProductAvailableVariantId
          );

          // R57b-5: bound references are stored so destroy() can actually
          // remove them - the old code bound fresh functions at add time and
          // passed unbound methods at remove time, so nothing was ever
          // removed.
          this._onStoreUpdate = this.handleCartStoreUpdate.bind(this);
          this._onReorder = this.handleReorderSellingPlans.bind(this);
          this._onSyncSubscription = this.handleSyncSubscription.bind(this);

          if (this._isStoreOwner) {
            window.addEventListener('cart-drawer-store:update', this._onStoreUpdate);
            window.addEventListener('cart-drawer-store:sync-subscription', this._onSyncSubscription);
          }
          window.addEventListener('cart-drawer-store:reorder-selling-plans', this._onReorder);

          // THE SAVING IS CHECKED AGAINST WHAT IS ON SCREEN, NOT AGAINST WHAT FIRED AN EVENT.
          //
          // Charlie, 20 Sep 2026, on an iPhone: the toggle was on and the card read "£7.60
          // saved every order" while the line beneath it said "One-time purchase" at the full
          // £76.00 and the summary agreed with the line. Not reproduced here - four sequences
          // on dev in Chrome at 390px (toggle on, add a second line, change the quantity,
          // reload) all converted the cart and rendered it, with the figure cleared correctly
          // when the basket moved. So the cause is NOT ESTABLISHED and this is not a fix for it.
          //
          // What it does do is make the state he photographed self-correcting. The existing
          // guard in handleCartStoreUpdate only runs on `cart-drawer-store:update`; a render
          // that reaches the DOM by any other route leaves a measured figure describing a
          // basket that is no longer on screen. `data-cart-total-pence` is rewritten by every
          // morph of the summary, whatever caused it, so observing that one attribute catches
          // all of them for the cost of one filtered observer.
          //
          // FINDING 3 OF THE FINAL ROUND, P2: this used to require a `[data-cart-total-pence]`
          // to exist AT INIT. On an empty cart there is none, `init()` never runs again, and
          // the observer was never installed for the rest of the page's life. The observer
          // watches `this.$el`'s whole subtree with an attribute filter, so the marker does not
          // need to exist yet - it is caught when a morph brings it in.
          this._totalObserver = null;
          if (this._isStoreOwner && this.$el && typeof MutationObserver === 'function') {
            this._totalObserver = new MutationObserver(() => this.dropStaleSaving());
            this._totalObserver.observe(this.$el, {
              subtree: true,
              attributes: true,
              attributeFilter: ['data-cart-total-pence'],
            });
          }
        },

        /**
         * Drop a measured saving once the basket on screen is not the basket it was
         * measured on. Safe to call at any time: it is a no-op unless there is a figure
         * AND a total to check it against.
         */
        dropStaleSaving() {
          const store = this.$store.cartDrawerStore;
          if (!(store.conversionSaving > 0) || store.conversionMeasuredTotal === null) return;
          const now = Number(this.parseCartTotalFromDom());
          if (isNaN(now) || now === store.conversionMeasuredTotal) return;
          store.conversionSaving = 0;
          store.conversionSavingText = '';
          store.conversionMeasuredTotal = null;
        },

        /**
         * Destroys the cart drawer alpine component.
         */
        destroy() {
          this._totalObserver?.disconnect();
          this._totalObserver = null;
          window.removeEventListener('cart-drawer-store:update', this._onStoreUpdate);
          window.removeEventListener('cart-drawer-store:reorder-selling-plans', this._onReorder);
          window.removeEventListener('cart-drawer-store:sync-subscription', this._onSyncSubscription);
          // R57c-3: only clears the seat if this instance is the one in it.
          window.cartController?.releaseStoreOwner?.(this);
          this._isStoreOwner = false;
        },

        /**
         * Read the cart total out of the freshly rendered DOM.
         * @returns {number|null} pence, or null when the marker is absent
         */
        parseCartTotalFromDom() {
          const el = document.querySelector('.alpine-cart-drawer [data-cart-total-pence]');
          if (el) return Number(el.getAttribute('data-cart-total-pence'));
          return null;
        },

        /**
         * PUT THE RUNTIME STATE BACK AFTER A MORPH. Codex round CART-DAY2 finding 1, P1.
         *
         * `data-conversion-phase` and `data-conversion-direction` are rendered server-side as
         * `idle` / `on` so that a morph restores a SAFE value rather than deleting the
         * attribute - that was the fix for the cover being stranded open. The cost, which this
         * finding names, is that a morph DURING a conversion resets them to `idle` and strips
         * `inert` and `cart-items-disabled` with them. The worker reconciles once per pass, so
         * on a multi-pass conversion the cover blinks off and every row unlocks while it is
         * still rewriting lines.
         *
         * Alpine will not re-apply on its own: the bindings are effects on elements morph
         * KEPT, so they only re-run when the value changes, and it has not. So the state is
         * written back imperatively, now and again on the next frame, because the morph and
         * this event are not ordered against each other.
         */
        reassertConversionState() {
          const store = this.$store.cartDrawerStore;

          const write = () => {
            document.querySelectorAll('.alpine-cart-drawer').forEach((el) => {
              el.setAttribute('data-conversion-phase', store.conversionPhase);
            });
            document.querySelectorAll('.cart-drawer__items-wrap').forEach((el) => {
              el.setAttribute('data-conversion-phase', store.conversionPhase);
              el.setAttribute('data-conversion-direction', store.conversionDirection);
              // Same reason as the two above: morph restores the server's `no` and Alpine's
              // binding will not re-run, so the confirmation that names a figure would stop
              // being selectable mid-beat.
              el.setAttribute('data-conversion-figure', store.conversionSaving > 0 ? 'yes' : 'no');
            });
            if (store.conversionBusy) this.disableCartItems();

            // FINDING 1 OF THE FINAL ROUND, P1. `data-conversion-phase` was put back here
            // and `inert` was not, although it is destroyed by exactly the same mechanism:
            // morph syncs an element's OWN attributes from server HTML that never carries a
            // runtime `inert`, and Alpine's `:inert` effect only re-runs when its expression
            // CHANGES - which it has not. So a morph during a multi-pass conversion left the
            // pointer lock restored and the keyboard lock gone: rows tabbable and Enter-able
            // while the worker was still rewriting lines.
            //
            // The two expressions below are the bindings themselves, restated. They must stay
            // in step with `snippets/ctr-cart-drawer.liquid` (`scroll-hint.cart-drawer__items`)
            // and `snippets/ctr-cart-summary.liquid` (`.cart__discount-link`). Setting the IDL
            // property rather than the attribute, because that is what Alpine does and it keeps
            // `false` from rendering as the string "false", which would be inert for ever.
            const itemsInert =
              store.conversionBusy || ['loading', 'saved'].includes(store.conversionPhase);
            document.querySelectorAll('.alpine-cart-drawer .cart-drawer__items').forEach((el) => {
              el.inert = itemsInert;
            });
            document.querySelectorAll('.alpine-cart-drawer .cart__discount-link').forEach((el) => {
              el.inert = store.conversionBusy;
            });
          };

          write();
          requestAnimationFrame(write);
        },

        handleCartStoreUpdate(event) {
          this.$store.cartDrawerStore.updateProducts(this.parseCartProductsJSON());

          // A MEASURED SAVING BELONGS TO THE BASKET IT WAS MEASURED ON. Seen on dev,
          // 20 Sep 2026: subscribe a five-line basket (card reads "£2.75 saved"), then add a
          // sixth line - the card still read £2.75 while the summary underneath it had moved
          // to £3.30. Two different figures a few pixels apart, and the smaller one was ours.
          //
          // The figure is the difference between two server totals taken either side of ONE
          // conversion. The moment the cart moves again it is describing a basket that no
          // longer exists, so it is dropped and the card falls back to the generic "You're
          // saving 10% on every delivery" strap, which is true of any basket.
          //
          // ANCHORED TO THE TOTAL, NOT THE PHASE. Codex round CART-DAY2 finding 4: guarding on
          // `idle` left the figure stale for any cart change landing during `saved` or
          // `revealing`, and the discount control is operable through both. The figure is
          // dropped as soon as the cart is not the cart it was measured on, whatever the
          // animation is doing, and kept when the totals still agree - which is what makes the
          // conversion's own reconcile safe without needing to know the phase.
          // One implementation, two triggers: this event, and the MutationObserver on
          // `data-cart-total-pence` set up in init().
          this.dropStaleSaving();

          // Codex round CART-DAY2 finding 1. Morph has just rewritten this subtree from the
          // server HTML, which carries the `idle` defaults and none of the runtime locks.
          this.reassertConversionState();
          // Re-check on every cart change, not just on load: emptying the cart
          // must wipe the persisted subscription, and a new item added to an
          // already-subscribed cart must adopt that cart's frequency.
          this.reconcileSubscriptionState();
        },

        /**
         * Keep the whole cart's subscription state consistent with its contents.
         * - Empty cart: full reset (toggle off, no stored plan) so a persisted
         *   ($persist) subscription can't bleed into the next cart.
         * - Cart with a subscribed line: toggle on, adopt that line's plan, and
         *   pull any other eligible line onto it so a newly added product matches
         *   the subscription already in the cart.
         * - Cart with only one-time items: toggle off, clear the stored plan.
         */
        /**
         * R57c-8. Keeps the visible toggle honest and starts the worker when the
         * cart does not match the target. It NEVER decides anything itself and it
         * holds no flags - every ordering bug in this file came from this function
         * reading state that the operation it triggered had not finished writing.
         */
        reconcileSubscriptionState() {
          const store = this.$store.cartDrawerStore;
          const cartLines = store.products || [];

          if (cartLines.length === 0) {
            store.isSubscriptionActive = false;
            store.setSellingPlan(null);
            store.desiredPlan = undefined;
            return;
          }

          const target = this.resolveTargetPlan(cartLines);

          store.isSubscriptionActive = target !== null;
          store.setSellingPlan(target);

          if (!this.cartMatchesTarget(cartLines, target)) this.updateCartItems();
        },

        /**
         * The single answer to "what should every line in this cart be on?".
         * An explicit choice wins; otherwise a subscribed line wins, which is
         * Charlie's rule: adding a one-time item to a subscribed cart subscribes
         * it, and so does adding a subscribed item to a one-time cart.
         * @param {any[]} cartLines
         * @returns {number | null}
         */
        resolveTargetPlan(cartLines) {
          const store = this.$store.cartDrawerStore;
          if (store.isWholesaleCustomer) return null;
          if (store.desiredPlan !== undefined) return store.desiredPlan;

          const subscribed = (cartLines || []).find(
            (p) => p && p.selling_plan_allocation && p.selling_plan_allocation.selling_plan && p.selling_plan_allocation.selling_plan.id
          );
          if (!subscribed) return null;

          const planId = Number(subscribed.selling_plan_allocation.selling_plan.id);
          if (this.sellingPlans.some((plan) => plan.id === planId)) return planId;

          // Codex round 6, P1. A subscribed cart whose frequency is no longer
          // offered (the retired 5 and 6 week plans) must KEEP its subscription
          // and take a valid default. Returning null here resolved it to "one-time"
          // and the worker began converting a paying subscriber off their plan -
          // and it ran at init() line ~116, BEFORE the block a few lines later that
          // exists precisely to repair an unavailable stored frequency.
          return this.getDefaultSellingPlan() || null;
        },

        /**
         * Whether the cart is already homogeneous on the target. Lines that cannot
         * carry a plan at all are not counted against it.
         * @param {any[]} cartLines
         * @param {number | null} target
         */
        cartMatchesTarget(cartLines, target) {
          this.cartProductsWithoutAvailableSellingPlans =
            this.parseCartProductsIdsWithoutSellingPlansJSON();

          return (cartLines || []).every((p) => {
            if (!p) return true;
            if (this.cartProductsWithoutAvailableSellingPlans.includes(p.id)) return true;
            if (this.cartProductsWithoutAvailableSellingPlans.includes(p.variant_id)) return true;
            if (p.discounted_price === 0) return true;
            const have = Number(p.selling_plan_allocation?.selling_plan?.id) || null;
            return have === target;
          });
        },

        /**
         * Handle subscription sync from Build Your Box or Quick Shop
         * @param {CustomEvent} event - Event with isSubscriptionActive, sellingPlan, and source in detail
         */
        handleSyncSubscription(event) {
          const { isSubscriptionActive, sellingPlan, source } = event.detail || {};
          
          // Ignore events dispatched by the cart drawer itself to prevent double updates
          if (source === "cart-drawer") return;
          
          
          // Update isSubscriptionActive (persisted in Alpine data)
          this.$store.cartDrawerStore.isSubscriptionActive = isSubscriptionActive;

          // Codex round 6, P2: this is an EXPLICIT intent from Build Your Box or
          // Quick Shop and it has to be recorded like one. R57c-8 moved the source
          // of truth to desiredPlan and updated only the toggle and the frequency
          // picker, so this path asked the worker to run and then had its request
          // ignored in favour of stale intent or a cart-derived target.
          this.$store.cartDrawerStore.desiredPlan = isSubscriptionActive
            ? Number(sellingPlan) || this.getDefaultSellingPlan() || null
            : null;
          this.$store.cartDrawerStore.conversionGeneration += 1;
          
          // Update sellingPlan in store (already done in box-builder, but ensure consistency)
          if (isSubscriptionActive && sellingPlan) {
            this.$store.cartDrawerStore.setSellingPlan(sellingPlan);
          } else if (!isSubscriptionActive) {
            this.$store.cartDrawerStore.setSellingPlan(null);
          }

          this.updateCartItems();
        },

        get totalPrices() {
          const totalPrices = {
            otp: 0,
            subscription: 0,
          };
  
          // Calculate base price (one-time purchase price)
          const otpPrice = this.$store.cartDrawerStore.products.reduce((total, item) => {
            // Use compare_at_price from selling plan allocation if available (original price)
            // Otherwise use original_line_price
            const price = item.selling_plan_allocation?.compare_at_price 
              ? item.selling_plan_allocation.compare_at_price * item.quantity
              : item.original_line_price;
            return total + price;
          }, 0);
  
          totalPrices.otp = otpPrice;
          // 10% discount for subscription
          totalPrices.subscription = Math.round(otpPrice * 0.9);
  
          return totalPrices;
        },

        /**
         * @returns {Array<{id: number, quantity: number, sellingPlan: number | null}>}
         */
        parseCartProductsJSON() {
          try {
            const cartProductsTemplate = this.$root.querySelector(
              ".cart-products-template"
            );
            if (!cartProductsTemplate) return [];
            return JSON.parse(cartProductsTemplate.textContent);
          } catch (error) {
            console.error("Error parsing cart products JSON:", error);
            return [];
          }
        },

        /**
         * @returns {Array<{id: number}>}
         */
        parseCartProductsIdsWithoutSellingPlansJSON() {
          try {
            const cartProductsTemplate = this.$root.querySelector(
              ".cart-products-without-selling-plans-template"
            );
            if (!cartProductsTemplate) return [];
            const ids = JSON.parse(cartProductsTemplate.textContent).map(
              /** @param {string} id */
              (id) => Number(id)
            );
            return ids;
          } catch (error) {
            console.error("Error parsing cart products JSON:", error);
            return [];
          }
        },

        /**
         * Returns the preselected selling plan ID if valid, then the 4-week plan, otherwise falls back to first.
         * @returns {number | null}
         */
        getDefaultSellingPlan() {
          const preselectedId = window.preselectedSellingPlan;

          if (preselectedId) {
            const found = this.sellingPlans.find(plan => plan.id === Number(preselectedId));
            if (found) return found.id;
          }

          const fourWeek = this.sellingPlans.find(plan => /4\s*week/i.test(plan.name));
          if (fourWeek) return fourWeek.id;

          return this.sellingPlans[0]?.id;
        },

        /**
         * THE CARD TRACK'S PAGER. Charlie, 20 Sep 2026: "swiper bar needs adding".
         *
         * DELIBERATELY NOT SWIPER. R80 replaced synchronous Swiper with CSS scroll snap on
         * purpose and STOREFRONT-RULES section 2 says not to resurrect it. The track here is
         * already a scroll-snap container, so a pager only has to read and set `scrollLeft` -
         * no library, no init cost, and the track still works with the pager removed.
         *
         * The dots are rendered by Liquid, one per cart line, so they survive a morph
         * re-render without JavaScript having to rebuild them.
         *
         * @param {Event} event
         */
        onCartTrackScroll(event) {
          const track = event.currentTarget;
          if (!(track instanceof HTMLElement)) return;

          const step = this.cartTrackStep(track);
          if (!step) return;

          // Clamped rather than trusted: a rubber-band overscroll on iOS reports a
          // scrollLeft past the end, which would light a dot that does not exist.
          const last = track.children.length - 1;
          const index = Math.round(track.scrollLeft / step);
          this.cartTrackIndex = Math.max(0, Math.min(last, index));
        },

        /**
         * @param {HTMLElement} track
         * @returns {number} one card plus one gap, in px
         */
        cartTrackStep(track) {
          const first = track.firstElementChild;
          if (!first) return 0;
          const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
          return first.getBoundingClientRect().width + gap;
        },

        /**
         * @param {number} direction - -1 back, 1 forward
         * @param {HTMLElement} from - the button, used to find its own track
         */
        moveCartTrack(direction, from) {
          const wrap = from.closest('.cart-drawer__items-wrap');
          const track = wrap && wrap.querySelector('.cart-items__table > tbody');
          if (!(track instanceof HTMLElement)) return;

          const step = this.cartTrackStep(track);
          if (!step) return;

          track.scrollBy({
            left: step * direction,
            behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth',
          });
        },

        onToggleSubscription() {
          const store = this.$store.cartDrawerStore;
          const target =
            !store.isSubscriptionActive || store.isWholesaleCustomer
              ? null
              : this.getDefaultSellingPlan();

          // R57c-8: an explicit choice is recorded once, and the generation bump is
          // what tells an already-running worker that it is now working toward the
          // wrong thing.
          store.desiredPlan = target;
          store.conversionGeneration += 1;
          store.setSellingPlan(target);
          // The customer's own tap - see transformationRequested on the store.
          store.transformationRequested = true;
          store.transformationKind = target === null ? 'off' : 'on';
          this.updateCartItems();

          // Dispatch sync event to notify sidebar (with source to prevent self-handling)
          window.dispatchEvent(
            new CustomEvent("cart-drawer-store:sync-subscription", {
              detail: {
                isSubscriptionActive: this.$store.cartDrawerStore.isSubscriptionActive,
                sellingPlan: this.$store.cartDrawerStore.sellingPlan,
                source: "cart-drawer",
              },
            })
          );
        },

        /**
         * @param {Event} event
         */
        onChangeCartFrequency(event) {
          if (!(event.target instanceof HTMLSelectElement)) return;

          const store = this.$store.cartDrawerStore;

          // R57c-8: picking a frequency is as explicit as flipping the toggle, so
          // it records intent and bumps the generation the same way. Without this
          // a frequency change mid-conversion would be silently overwritten by the
          // worker still driving toward the old plan.
          const target = store.isWholesaleCustomer ? null : Number(event.target.value) || null;

          store.desiredPlan = target;
          store.conversionGeneration += 1;
          store.setSellingPlan(target);
          store.transformationRequested = true;
          // Changing the cadence is neither turning it on nor off - see transformationKind.
          store.transformationKind = 'frequency';
          this.updateCartItems();

          if (store.isWholesaleCustomer) return;

          // Dispatch sync event to notify sidebar (with source to prevent self-handling)
          window.dispatchEvent(
            new CustomEvent("cart-drawer-store:sync-subscription", {
              detail: {
                isSubscriptionActive: this.$store.cartDrawerStore.isSubscriptionActive,
                sellingPlan: this.$store.cartDrawerStore.sellingPlan,
                source: "cart-drawer",
              },
            })
          );
        },

        /**
         * Normalises a properties object so two of them can be compared without
         * caring about key order or number-versus-string.
         * @param {Record<string, any> | null | undefined} props
         * @returns {string}
         */
        normaliseProperties(props) {
          const source = props || {};
          return JSON.stringify(
            Object.keys(source)
              .sort()
              .map((key) => [key, String(source[key])])
          );
        },

        /**
         * R57c-5, replacing getBody(). Given the CURRENT SERVER CART, returns the
         * line changes still outstanding to bring every line onto (or off) the
         * selected plan.
         *
         * The old version reshaped the DOM's copy of the cart into an `items`
         * array for a wholesale clear-and-re-add. This addresses lines instead,
         * which means two changes of substance:
         *
         * - **Lines are addressed by their current Shopify key, read from the
         *   server response, not by variant id.** §4: never by position, and now
         *   never by a key captured before the previous change either, because
         *   applying a plan rewrites the key.
         * - **It is idempotent and re-entrant.** It is re-run against a fresh cart
         *   after every single change, and only emits work for lines that do not
         *   already match the target, so the sequence converges instead of
         *   reapplying itself.
         *
         * Property handling is a deliberate FAITHFUL PORT of the old behaviour
         * rather than the more obvious `/cart/change.js` idiom. Omitting
         * `properties` would make Shopify preserve whatever the line already
         * carries, which reads better but is a live behaviour change: today a line
         * switching from subscription to one-time LOSES its `_dynamicBoxIndex` and
         * `_dynamicBoxId`, because the re-add simply did not carry them. Sending
         * the properties explicitly keeps that. Whether it SHOULD is a separate
         * decision and a separate release - mixing it into the fix for a
         * cart-destroying bug would make an incident impossible to attribute.
         * @param {Record<string, any>} cart - the cart as the server last reported it
         * @returns {Record<string, any>[]} change bodies, one per line still needing work
         */
        planLineChanges(cart, targetPlan) {
          // new-cart-drawer, 20 Sep 2026. THE BEFORE HALF OF THE MEASURED SAVING.
          //
          // This is the first thing the planner sees on every pass, and on the FIRST
          // pass of a beat `cart` is the server's own read of the basket before a
          // single line has changed. That is the only moment the pre-conversion total
          // exists, so it is taken here rather than reconstructed later.
          //
          // Guarded on `conversionBaseline === null` because the planner runs once per
          // pass AND once more at the end of changeLines() to verify completion - that
          // last call carries the CONVERTED cart, and letting it overwrite the baseline
          // would report every saving as zero.
          //
          // Guarded on phase `loading` because the planner is reachable outside a beat
          // (a reconcile after an add, for one) and a baseline taken there belongs to
          // no beat at all.
          const beatStore = this.$store.cartDrawerStore;
          if (beatStore.conversionPhase === 'loading' && beatStore.conversionBaseline === null) {
            beatStore.conversionBaseline = Number(cart.total_price);
            if (cart.currency) beatStore.conversionCurrency = cart.currency;
          }

          // R84, Codex round 33 finding 4. Fail-closed invariant at the boundary.
          // If a subscription target reaches the planner while box readiness does
          // NOT hold, the plan-only comparison below would call every line settled
          // and the caller would spend desiredPlan on a cart that never got its
          // box properties. That is the P1 exactly.
          //
          // Throwing is the correct shape here rather than fabricating a change
          // body: cart-controller.js catches a planner throw, stops, and reports
          // ok:false, so updateCartItems keeps the intent. No request is posted,
          // and an unavailable dependency is never mistaken for a Shopify failure.
          if (targetPlan !== null) {
            const readiness = this.$store.cartDrawerStore.dynamicBoxReadiness(
              this.bundleProductId,
              this.bundleProductAvailableVariantId
            );

            if (readiness !== 'ready' && readiness !== 'not-applicable') {
              throw new Error(
                `planLineChanges refused a subscription plan: dynamic-box readiness is "${readiness}"`
              );
            }
          }

          // Kept from getBody(): the store refresh is a side effect the rebuild
          // has always performed at this point in the sequence (§4 - the second
          // updateProducts per rebuild is not a double handler).
          this.$store.cartDrawerStore.updateProducts(this.parseCartProductsJSON());

          const products = this.$store.cartDrawerStore.products;

          // R90b, found while sweeping for the same class Codex round 35 was
          // about. parseCartProductsJSON() returns [] both when the template is
          // missing and when JSON.parse throws, and every cart line then fails
          // to match, so the planner emitted no work at all. changeLines() then
          // reported ok:true with nothing outstanding - a conversion that did
          // nothing and called itself a success, which is the silent failure
          // class with the worst possible disguise.
          //
          // Throwing is the shape the planner already uses for an unavailable
          // dependency: the controller stops, posts nothing, and reports
          // ok:false, and updateCartItems() surfaces once when it gives up.
          if ((cart.items || []).length > 0 && products.length === 0) {
            throw new Error(
              'planLineChanges has no parsed cart products for a non-empty cart; the cart-products-template is missing or unparseable'
            );
          }

          this.cartProductsWithoutAvailableSellingPlans =
            this.parseCartProductsIdsWithoutSellingPlansJSON();

          // R57c-8: the target arrives as an argument. Reading it from the store
          // mid-conversion was one more way to act on state that had moved.
          const dynamicBoxId = this.$store.cartDrawerStore.dynamicBoxId;
          const isDynamicBoxEnabled = dynamicBoxId ? true : false;

          // R90b. Second half of the round 35 P1, and the one that explains the
          // boxless orders that kept arriving AFTER the guard went in.
          //
          // `_dynamicBoxId` is a BOX-level value, not a per-variant one:
          // `withDynamicBox()` in cart-controller.js writes the same id and
          // `_dynamicBoxIndex: 0` onto every add carrying an approved plan, with
          // no reference to which variant is being added. This planner used to
          // write those properties only for variants present in
          // `dynamicBoxSelectableProductVariants`, so the add path and the
          // change path disagreed about the same cart: a subscribed variant
          // outside Skio's selectable list took the plan here and got NO box
          // properties, which is a boxless subscription created by the drawer.
          //
          // The membership test is gone. A line either takes the plan WITH the
          // box properties or does not take the plan at all.
          if (targetPlan !== null && !isDynamicBoxEnabled) {
            throw new Error(
              'planLineChanges refused a subscription plan: dynamic-box readiness passed but no box id is present'
            );
          }

          /** @type {Record<string, any>[]} */
          const work = [];

          (cart.items || []).forEach((item) => {
            const product = products.find(
              /** @param {{id: number, variant_id: number}} candidate */
              (candidate) => candidate.variant_id === item.variant_id || candidate.id === item.variant_id
            );

            // Codex round 4, P2: a line we cannot match must be LEFT ALONE, not
            // marked ineligible. `ineligible` does not mean "skip" - it means
            // "force to one-time" - so an unmatched line was having its
            // subscription actively REMOVED by a forward conversion. The comment
            // that used to sit here said "treated as ineligible rather than
            // guessed at", which described the behaviour intended rather than the
            // behaviour written: the exact `product-media.liquid` mistake in §4.
            if (!product) return;

            // Codex round 4, P2: the discount test reads the SERVER line, not the
            // store product. `products.find()` matches on variant id, and a cart
            // can hold two lines of the same variant distinguished only by their
            // properties - that is what the line key exists for. Taking
            // discounted_price from whichever copy happened to be first meant a
            // paid line could be skipped, or a zero-price line put onto a plan.
            // Plan AVAILABILITY is genuinely a product-level fact, so the two
            // list checks below still use the matched product.
            const isDiscount = item.discounted_price === 0;
            const ineligible =
              this.cartProductsWithoutAvailableSellingPlans.includes(product.id) ||
              this.cartProductsWithoutAvailableSellingPlans.includes(product.variant_id) ||
              isDiscount;

            const wantPlan = ineligible ? null : targetPlan;
            const wantsBundleProperties = !ineligible && isDynamicBoxEnabled && targetPlan;
            // R90c, Codex round 36 finding 1. This built the box keys ALONE and
            // sent them as the line's `properties`, and the note below records
            // that a non-empty object OVERWRITES. So converting a line destroyed
            // every other property it carried - including `_pack_kit`, the
            // Discount Kit identifier configured in ctr-exalt-product.liquid.
            //
            // The add path never had this bug: withDynamicBox() in
            // cart-controller.js spreads `...(body.properties || {})` before
            // stamping the two keys. The two paths now agree about the same cart,
            // which is the actual requirement - not merely that both write a box id.
            const wantProperties = wantsBundleProperties
              ? { ...(item.properties || {}), _dynamicBoxIndex: 0, _dynamicBoxId: dynamicBoxId }
              : {};

            const havePlan = item.selling_plan_allocation?.selling_plan?.id ?? null;
            // R57c-5b: only hold a line "unsettled" on properties when we are
            // actually going to WRITE some. `/cart/change.js` cannot clear
            // properties (see the note below), so demanding an empty match would
            // re-emit the same line for ever until the no-progress guard caught it.
            const wantsPropertyWrite = Object.keys(wantProperties).length > 0;
            const settled =
              havePlan === wantPlan &&
              (!wantsPropertyWrite ||
                this.normaliseProperties(item.properties) === this.normaliseProperties(wantProperties));

            if (settled) return;

            /** @type {Record<string, any>} */
            const change = {
              // The line key as the server reported it in THIS response.
              id: item.key,
              // Always sent: Shopify assumes 1 when quantity is omitted from a
              // selling-plan change, which would silently shrink the line.
              quantity: item.quantity,
              selling_plan: wantPlan,
            };

            // R57c-5b: send `properties` only when there is something to SET.
            // Measured on dev: `/cart/change.js` reads `properties: {}` as
            // "unspecified" and leaves the line's existing properties untouched.
            // It neither clears them nor errors - it just burns a request. A
            // NON-empty object does overwrite, which is how the dynamic-box keys
            // get written on the way in.
            //
            // The consequence, stated plainly because it is a real behaviour
            // change: a line converted subscription -> one-time now KEEPS its
            // `_dynamicBoxIndex` / `_dynamicBoxId`, where the old clear-and-re-add
            // dropped them by starting from nothing. Checked against the last 40
            // live orders: every subscribed line carries a box id and no one-time
            // line ever has, so this is a new shape in the order feed. It is
            // judged inert because Skio's box grouping only applies to lines that
            // carry a selling plan, and both keys are underscore-prefixed and
            // therefore hidden everywhere customer-facing.
            if (Object.keys(wantProperties).length > 0) change.properties = wantProperties;

            work.push(change);
          });

          return work;
        },

        /**
         * R57c-8: the one conversion worker. Store-owned, generation-aware, and it
         * runs until the SERVER cart is unmixed - which is what makes "never a
         * mixed basket" a property of the design rather than something we hope a
         * later reconcile re-triggers.
         *
         * Codex round 5 killed the previous shape on three counts: a shared
         * boolean is not a concurrency guard, a retry timer with no generation or
         * teardown check wakes into a world that has moved on, and an in-flight
         * early return stranded any failed subscribe-direction conversion because
         * nothing scheduled another pass. All three stop existing here.
         */
        async updateCartItems() {
          const store = this.$store.cartDrawerStore;

          if (store.conversionRunning) {
            store.conversionPending = true;
            return;
          }
          if (!window.cartController?.changeLines) {
            // R90b, Codex round 35 finding 3. The customer has just toggled
            // subscribe. Returning with a console line leaves the toggle looking
            // like it worked and the basket unchanged, which is the silent
            // failure class this release exists to remove.
            const error = new Error('cart-controller.js is not loaded; the cart rebuild was not sent');
            console.error('[cart-drawer-alp]', error.message);
            window.ctrNotifyCartFailure?.(
              'Sorry, your basket could not be updated. Please refresh the page and try again.',
              'cart-drawer-alp',
              error
            );
            // A drained hand-off may have left a beat open - see abortConversionBeat.
            store.abortConversionBeat(true);
            return;
          }

          // R84, Codex round 33 finding 1. Box readiness is a PRECONDITION of a
          // subscription-direction conversion, checked BEFORE the mutex is taken
          // and before the cart items are disabled.
          //
          // A one-time conversion (target === null) needs no box data, and neither
          // does a wholesale customer, so neither is gated.
          //
          // ⚠️ This does NOT make the conversion await the request. Round 25
          // finding 3 forbids that and still holds: nothing waits on the network
          // here. When the request is in flight the worker EXITS and re-enters
          // from a single continuation once it settles.
          // new-cart-drawer: hoisted out of the `if` it used to be computed inside, so
          // the beat below can be opened knowing which direction it is going. Same call,
          // same value, one evaluation instead of two.
          const beatTarget = this.resolveTargetPlan(store.products || []);

          if (beatTarget !== null) {
            const readiness = store.dynamicBoxReadiness(
              this.bundleProductId,
              this.bundleProductAvailableVariantId
            );

            if (readiness === 'pending' || readiness === 'unavailable') {
              // The intent is NOT consumed and NOT converged. Both are the P1.
              const generation = store.conversionGeneration;

              if (store.dynamicBoxResumeGeneration !== generation) {
                store.dynamicBoxResumeGeneration = generation;

                store
                  .ensureDynamicBox(this.bundleProductId, this.bundleProductAvailableVariantId)
                  .then((result) => {
                    store.dynamicBoxResumeGeneration = null;

                    // THE GENERATION CHECK COMES FIRST, AND IT IS THE WHOLE FIX FOR FINDING 1
                    // OF THE CONFIRMATION ROUND (P1).
                    //
                    // The toggle stays operable while this wait is outstanding, so a customer
                    // can switch Subscribe & Save straight back OFF. The OFF beat is a
                    // DIFFERENT beat. This continuation used to test `result.ready` before it
                    // tested whose intent it was, so a late "not ready" from the abandoned ON
                    // attempt announced "subscriptions are unavailable" over an OFF conversion
                    // that was succeeding, and `abortConversionBeat(true)` tore down a beat it
                    // did not own. The ready path had the mirror of it: it aborted the newer
                    // beat silently.
                    //
                    // A superseded continuation now touches NOTHING. It does not announce, it
                    // does not abort, it does not convert. Whoever holds the newer intent owns
                    // the beat and will finish it. Clearing `dynamicBoxResumeGeneration` above
                    // is the one thing it must still do, because `conversionBusy` reads it and
                    // the locks would otherwise stay up for the rest of the wait.
                    if (store.conversionGeneration !== generation) return;

                    // Not-ready STOPS. Re-entering here is what would turn a
                    // failing dependency into a request storm, and round 33
                    // finding 3 is explicit that the failure result must not
                    // schedule another attempt. A later explicit call, or the
                    // next page load, retries against the cleared guard.
                    if (!result?.ready) {
                      window.ctrNotifyCartFailure?.(
                        'Sorry, subscriptions are unavailable right now. Please try again in a moment.',
                        'cart-drawer-alp',
                        new Error(`dynamic box not ready: ${result?.reason ?? 'unknown'}`)
                      );
                      store.abortConversionBeat(true);
                      return;
                    }

                    if (store.desiredPlan === undefined) { store.abortConversionBeat(false); return; }

                    this.updateCartItems();
                  })
                  .catch((error) => {
                    // A REJECTION MUST STILL CLEAR THE MARKER. `ensureDynamicBox` can reject -
                    // its own request carries a timeout - and `.then` alone would leave
                    // `dynamicBoxResumeGeneration` set, which `conversionBusy` reads, so
                    // checkout and the discount control would stay locked for the rest of the
                    // page's life. Same ownership rule as above: a superseded one says nothing.
                    const superseded = store.conversionGeneration !== generation;
                    store.dynamicBoxResumeGeneration = null;
                    if (superseded) return;
                    window.ctrNotifyCartFailure?.(
                      'Sorry, subscriptions are unavailable right now. Please try again in a moment.',
                      'cart-drawer-alp',
                      error instanceof Error ? error : new Error(String(error))
                    );
                    store.abortConversionBeat(true);
                  });
              }

              // The resume above will open its own beat when it lands. Holding this one
              // open across a network wait is what left the cover up for ever.
              store.abortConversionBeat(false);
              return;
            }
          }

          store.conversionRunning = true;

          // The beat starts with the mutex and OUTLIVES it: the cover, the success beat
          // and the staggered reveal are all driven from `conversionPhase`, closed in
          // the `finally` below. Every early return above happens before this line, so
          // a conversion that never starts never puts a cover on screen.
          //
          // ...AND ONLY WHEN THE CUSTOMER ASKED. A reconcile after an ordinary add runs
          // the same worker over the same lines; it gets no cover. An already-open beat
          // always continues, which is how the `conversionPending` drain keeps one going
          // across two workers. `beat` is null for a silent run and the `finally` below
          // leaves the phase alone.
          const wantsBeat = store.transformationRequested || store.conversionPhase !== 'idle';
          store.transformationRequested = false;
          // The recorded kind wins, because only the handler knows whether the customer
          // changed the cadence or the subscription. The derived value is the fallback for a
          // beat that was joined rather than opened.
          const beatKind = store.transformationKind || (beatTarget !== null ? 'on' : 'off');
          store.transformationKind = null;
          const beat = wantsBeat ? store.beginConversionBeat(beatKind) : null;
          this.disableCartItems();

          // R90c, Codex round 36 finding 4. One customer intent gets exactly one
          // message, from wherever this worker decides to stop trying. Declared
          // outside the try so the catch below shares the same latch: the two
          // used to be able to fire in sequence for a single toggle.
          let notified = false;
          /**
           * @param {string} [message] - undefined falls back to the house copy.
           * @param {unknown} [error]
           */
          const notifyOnce = (message, error) => {
            if (notified) return;
            notified = true;
            // Codex round SUBSAVE-TRANSFORM finding 1. Also record it on the beat, which
            // outlives this worker - see the comment on conversionBeatFailed.
            store.conversionBeatFailed = true;
            window.ctrNotifyCartFailure?.(message, 'cart-drawer-alp', error ?? null);
          };

          // R91, Codex round 37 finding 8. The one-time fallback used to announce
          // "it has been left as a subscription" at the moment it DECIDED to give up,
          // before the passes that actually restore the subscription had run. If those
          // passes then exhausted, the latch above swallowed the real failure and the
          // customer was left holding exactly one message that was false.
          //
          // The latch is right and stays - R90b's complaint was one toggle painting six
          // messages. What was wrong was announcing an outcome before it was an outcome.
          // The decision now only records itself here, and each EXIT says the thing that
          // is true at that exit.
          let oneTimeAbandoned = false;

          // new-cart-drawer: `lastResult` is declared out here rather than inside the
          // try because the `finally` needs the settled cart's total_price - the AFTER
          // half of the measured saving. Nothing else about it changed.
          /** @type {Record<string, any> | null} */
          let lastResult = null;

          try {
            let attempts = 0;
            // R91a. The generation this worker last observed, so the exhausted branch
            // below can tell a non-intent re-entry from a genuine new intent that
            // landed during the final wait.
            let lastGeneration = store.conversionGeneration;

            while (attempts < 6) {
              const generation = store.conversionGeneration;
              lastGeneration = generation;
              const target = this.resolveTargetPlan(store.products || []);

              // R90b, Codex round 35 finding 2. This worker runs the same
              // customer intent up to six times, and changeLines() surfaced on
              // every failed pass, so one toggle could paint six messages. The
              // message is owned here now and said exactly once, when the worker
              // stops trying - see the exhausted branch and the catch below.
              // new-cart-drawer, 21 Sep 2026: every line in two requests, not one
              // request per line - see swapLines() in cart-controller.js. The
              // fallback keeps a cached old controller working during a deploy.
              const convert = window.cartController.swapLines || window.cartController.changeLines;
              const result = await convert(
                /** @param {Record<string, any>} cart */
                (cart) => this.planLineChanges(cart, target),
                { source: 'cart-drawer-alp', surface: false }
              );

              lastResult = result;


              // A newer intent landed while we were working. Start again against
              // it rather than finishing a conversion nobody wants any more.
              if (store.conversionGeneration !== generation) {
                attempts = 0;
                // R91b, Codex round 38 finding 2. `oneTimeAbandoned` records a decision
                // taken about the PREVIOUS intent. A new intent invalidates that decision
                // exactly as it invalidates the attempt budget, and leaving it set let the
                // settled exit below announce "left as a subscription" over a basket the
                // restarted loop had just converted successfully to one-time. That is a
                // false statement shown to a customer - the class R91 finding 8 removed.
                oneTimeAbandoned = false;
                continue;
              }
              // A Shopify REFUSAL is a decision, not a transient failure. It was
              // being retried five more times over seven seconds - six identical
              // requests Shopify will refuse identically - and with per-pass
              // surfacing suppressed the customer now waits all seven seconds to
              // be told. Stop on it, say it once, and use Shopify's own copy.
              if (!result.ok && result.data && (result.data.errors || result.data.status)) {
                console.error('[cart-drawer-alp] Shopify refused the conversion; not retrying', result.data);
                // R91, Codex round 37 finding 7. This picked `description || message`
                // by hand while the branch above accepts `errors` as proof of a
                // refusal, so an errors-only refusal was recognised and then reported
                // in house copy. customerMessage() renders all three shapes and is the
                // same function the controller uses, so the wording no longer depends
                // on which caller happens to be surfacing.
                notifyOnce(
                  window.cartController?.customerMessage?.(result.data) ?? undefined,
                  result.error ?? null
                );
                store.desiredPlan = undefined;
                break;
              }

              if (result.ok && !result.outstanding) {
                // Codex round 6, P2: the explicit choice is now SPENT. Leaving it
                // set made every later cart change obey it - a subscribed item
                // added after a successful one-time toggle was dragged back to
                // one-time, which is the opposite of the rule that a subscribed
                // line wins.
                store.desiredPlan = undefined;

                // R91, finding 8. Reached after an abandoned one-time conversion, this
                // is the ONLY point at which "left as a subscription" is a fact: the
                // cart is settled and homogeneous. Said here, it is true.
                if (oneTimeAbandoned) {
                  notifyOnce(
                    'Sorry, we could not switch your basket to a one-off purchase, so it has been left as a subscription. Please try again or contact us.'
                  );
                }
                break;
              }

              // R91a. `conversionPending` is raised by ANY re-entrant updateCartItems()
              // that arrives while this worker holds the mutex, and the commonest
              // source is this worker's OWN reconcile coming back round - not the
              // customer. Refilling the budget on that made `attempts` unreachable.
              //
              // Measured on dev theme 147519373427 with /cart/change induced to answer
              // 503: the flag was raised SIX times in an eight-second window across
              // four passes, so neither the abandonment branch (attempts >= 3) nor the
              // exhausted branch (attempts >= 6) was ever reached. 49 requests over
              // ~90s, no banner, no live-region announcement, no cart:error - the
              // worker retried in silence for as long as the transport stayed down,
              // and R91's exhausted-exit copy was dead code.
              //
              // Only a GENUINE NEW INTENT refills the budget, and that already has its
              // own signal: `conversionGeneration`, bumped by the four sites that also
              // set `desiredPlan`, checked above. A pending re-entry still earns
              // another pass - the cart may really have moved - but it SPENDS an
              // attempt. The trade is stated rather than implied: a conversion that
              // genuinely needs more than six passes now gives up and says so, which is
              // what `while (attempts < 6)` was written to mean.
              if (store.conversionPending) {
                store.conversionPending = false;
              }

              attempts += 1;

              // One-time is the only direction we will give up on, and only after
              // a budget. Clearing the explicit choice lets resolveTargetPlan fall
              // back to "a subscribed line wins", so the remaining passes drive the
              // cart to homogeneous rather than leaving it mixed.
              if (attempts >= 3 && target === null && store.desiredPlan === null) {
                console.error('[cart-drawer-alp] one-time conversion could not complete; converging on the subscription instead so the basket is not left mixed');
                // R90c, Codex round 36 finding 4 established that this branch owes the
                // customer a message: it is the one branch where they end up with the
                // OPPOSITE of what they asked for. R91, finding 8, keeps the debt and
                // moves the payment. Only RECORD the decision here; the exits pay it,
                // because whether the basket really ends up "left as a subscription"
                // is not known until the following passes have run.
                oneTimeAbandoned = true;
                store.desiredPlan = undefined;
              }

              await new Promise((resolve) => setTimeout(resolve, 1200));

              // Torn down during the wait: this instance must not keep mutating a
              // global store through a disconnected root.
              if (!this.$root || !this.$root.isConnected) return;
            }

            if (attempts >= 6) {
              console.error('[cart-drawer-alp] conversion worker exhausted its passes; cart may still be mixed');
              // R91, finding 8, and the invariant Charlie confirmed on 22 Aug 2026: a
              // basket is all-subscription or all-one-time, and MIXED MUST NOT EXIST.
              // Reaching here means the worker could not guarantee that, so this is not
              // an ordinary "please try again" - it is the one outcome the customer must
              // check before paying. Shopify's own copy when it gave us any, this
              // otherwise, and never raw error text.
              // customerMessage() returns HOUSE COPY rather than nothing when a body
              // carries no wording, so it cannot be used with `||` - the fallback below
              // would never be reached. Ask first whether Shopify actually said anything.
              const shopifySpoke = Boolean(
                lastResult?.data &&
                  (lastResult.data.description || lastResult.data.message || lastResult.data.errors)
              );
              notifyOnce(
                shopifySpoke
                  ? window.cartController?.customerMessage?.(lastResult.data)
                  : 'Sorry, we could not finish updating your basket. Please review it before checking out.',
                lastResult?.error ?? null
              );
              // Codex round 6, P2: do not leave an explicit intent behind that a
              // later pass would keep obeying. Cleared, the next cart change
              // derives the target and a subscribed line wins, which is the rule.
              store.desiredPlan = undefined;

              // R91a. The `finally` below drains `conversionPending` into a fresh
              // worker with a fresh budget. After exhaustion that would paint a SECOND
              // message for the same intent, which is precisely what the notifyOnce
              // latch exists to prevent. Discard a flag raised by a non-intent
              // re-entry; keep one that arrived with a genuine new intent, because that
              // bumps `conversionGeneration` and is still worth another worker.
              if (store.conversionGeneration === lastGeneration) {
                store.conversionPending = false;
              }
            }
          } catch (error) {
            console.error('[cart-drawer-alp] conversion worker failed', error);
            notifyOnce('Sorry, your basket could not be updated. Please try again.', error);
          } finally {
            store.conversionRunning = false;
            this.enableCartItems();

            // Drain anything that arrived while we held the mutex.
            if (store.conversionPending) {
              store.conversionPending = false;
              // THE BEAT IS NOT CLOSED HERE. The next worker joins the same beat and
              // keeps the same baseline, so the cover stays up rather than flashing
              // away and back between two passes of one customer intent.
              setTimeout(() => this.updateCartItems(), 0);
            } else {
              // `notified` is the worker's own one-message-per-intent latch, so it is
              // already the honest answer to "did this end badly", including the branch
              // where a one-time conversion was abandoned and the basket was left as a
              // subscription. Reusing it means the beat cannot disagree with the banner.
              //
              // The settled cart is only trustworthy as an AFTER total when the run was
              // ok; on a refusal `result.data` is Shopify's error body, not a cart.
              const afterTotal =
                lastResult && lastResult.ok && lastResult.data
                  ? Number(lastResult.data.total_price)
                  : null;

              if (beat !== null) store.finishConversionBeat(beat, !notified, afterTotal);
              // The rows on screen are checked against the cart the server just settled on.
              this.verifyRenderedCart(
                lastResult && lastResult.ok && lastResult.data ? lastResult.data : null
              );
            }
          }
        },

        /**
         * THE ROWS ARE CHECKED AGAINST THE SETTLED CART, AND REPAIRED ONCE IF THEY DISAGREE.
         *
         * Charlie, 20 Sep 2026, on an iPhone: Subscribe & Save on, the card claiming a saving,
         * and the line still reading "One-time purchase" at the full price. He then established
         * the important half - "if i then refresh the page the one time changes to S&S" - so the
         * CART was converted and only the render was stale. Nobody was ever going to be charged
         * the wrong thing; the drawer was showing a basket that no longer existed.
         *
         * NOT REPRODUCED here, and the cause is NOT ESTABLISHED. Ruled out on dev in Chrome:
         * a plain run, a run at Slow 4G with 4x CPU (document responseEnd 1,162 ms), a variant
         * with no selling plan (all four carry 12 allocations), and localStorage throwing on
         * every write the way Safari private browsing used to - 8 throws, rendered correctly
         * anyway. What is left is iOS Safari's own engine and the shopifypreview.com share
         * origin, neither of which this machine can drive.
         *
         * So this does not diagnose it, it repairs it. `afterTotal` is the total the SERVER
         * reported for the settled cart; `data-cart-total-pence` is what the summary is
         * currently showing. If they disagree, the render did not arrive, and
         * `cartController.reconcile` is the existing one-GET path that throws the DOM away and
         * asks for the truth - the same call the controller already uses after a mutation it
         * could not trust.
         *
         * ONE ATTEMPT, NEVER A LOOP. Nothing re-checks after the reconcile, and the 600 ms wait
         * is there so the mutation's own render gets to land first and this stays a no-op in
         * the normal case.
         *
         * THE TOTAL IS NOT ENOUGH ON ITS OWN. Finding 2 of the final round, P2: a frequency
         * change between two equally priced plans leaves the total untouched, so a stale
         * cadence label passed the check and was never repaired - which is the one outcome
         * where the price is guaranteed NOT to move. So the plan names the server settled on
         * are checked as well, and a name the drawer is not showing is the same evidence as a
         * total it is not showing.
         * @param {Record<string, any>|null} settledCart - the cart the server reported for the
         *   settled basket, or null when the run produced no trustworthy cart
         */
        async verifyRenderedCart(settledCart) {
          if (!settledCart || typeof window.cartController?.reconcile !== 'function') return;

          const store = this.$store.cartDrawerStore;
          const generation = store.conversionGeneration;
          const settledTotal = Number(settledCart.total_price);

          // The cadence each settled line is on, IN ORDER, with `null` for a one-time line.
          // Both cases render `.cart-items__selling-plan-name`; only the one-time branch adds
          // the `--one-time` modifier, so the class is what is compared and no English string
          // is duplicated into this file.
          const settledPlans = Array.isArray(settledCart.items)
            ? settledCart.items.map(
                (item) => item?.selling_plan_allocation?.selling_plan?.name || null
              )
            : [];

          await new Promise((resolve) => setTimeout(resolve, 600));

          // FINDING 4 OF THE CONFIRMATION ROUND, P2. `reconcile` is a GET that morphs whatever
          // it gets back, and it is explicitly NOT serialised against mutations, so firing it
          // while something newer is in flight can paint older HTML over a newer render. It is
          // therefore only allowed to run from a quiet drawer, and only for the intent that
          // opened it: a newer generation, or a worker that has started again, means the repair
          // is not ours to make and the newer pass will render its own truth.
          if (
            store.conversionGeneration !== generation ||
            store.conversionRunning ||
            store.conversionPending
          ) {
            return;
          }

          const disagreement = this.renderedCartDisagreement(settledTotal, settledPlans);
          if (!disagreement) return;

          console.warn(
            '[cart-drawer] the drawer was showing a basket the cart no longer has; reconciling',
            disagreement
          );
          await window.cartController.reconcile('conversion-render-check');

          // ONE ATTEMPT. If it is still wrong the repair has failed and something else is
          // driving the DOM; say so with the numbers rather than firing again.
          const stillWrong = this.renderedCartDisagreement(settledTotal, settledPlans);
          if (stillWrong) {
            console.error(
              '[cart-drawer] reconcile did not settle the drawer; it is showing a basket the cart does not have',
              stillWrong
            );
          }
        },

        /**
         * Compare what the drawer is rendering against a cart the server reported.
         *
         * FINDING 3 OF THE CONFIRMATION ROUND, P2. The first version compared plan names as a
         * SET and then threw the answer away whenever the rendered set was empty - which is
         * precisely the reported symptom, a subscribed cart rendered as one-time. It was also
         * silent in the other direction, because a one-time settled cart contributes no names
         * to look for. Comparing the ORDERED list, with a one-time line represented as `null`,
         * answers both, and a length difference answers a line that was added or removed.
         *
         * WHAT IT STILL DOES NOT CHECK, stated rather than implied: per-line quantity and
         * per-line price. The total covers them in aggregate; two lines swapping quantity at an
         * identical total would pass.
         *
         * @param {number} settledTotal - the server's total in pence
         * @param {(string|null)[]} settledPlans - plan name per line, null for one-time
         * @returns {Record<string, any>|null} what disagreed, or null when it all agrees
         */
        renderedCartDisagreement(settledTotal, settledPlans) {
          const shown = Number(this.parseCartTotalFromDom());
          const totalDisagrees = !isNaN(settledTotal) && !isNaN(shown) && shown !== settledTotal;

          const rendered = [
            ...document.querySelectorAll('.alpine-cart-drawer .cart-items__selling-plan-name'),
          ].map((el) =>
            el.classList.contains('cart-items__selling-plan-name--one-time')
              ? null
              : el.textContent.replace(/\s+/g, ' ').trim()
          );

          // The whole plan block is gated on the basket holding a subscribable line, so an
          // all-merch basket renders none of these. That is only silence, not disagreement -
          // unless the settled cart says a line IS on a plan, in which case a label was owed.
          const planDisagrees =
            rendered.length === 0
              ? settledPlans.some((name) => name !== null)
              : rendered.length !== settledPlans.length ||
                settledPlans.some((name, i) => name !== rendered[i]);

          if (!totalDisagrees && !planDisagrees) return null;
          return { shown, settledTotal, settledPlans, rendered, totalDisagrees, planDisagrees };
        },
        /**
         * Disables the cart items.
         */
        disableCartItems() {
          const cartItemsComponents = document.querySelectorAll(
            "cart-items-component"
          );
          cartItemsComponents.forEach((item) => {
            if (item instanceof HTMLElement) {
              item.classList.add("cart-items-disabled");
            }
          });
        },

        /**
         * Enables the cart items.
         */
        enableCartItems() {
          const cartItemsComponents = document.querySelectorAll(
            "cart-items-component"
          );
          cartItemsComponents.forEach((item) => {
            if (item instanceof HTMLElement) {
              item.classList.remove("cart-items-disabled");
            }
          });
        },
        // R57c-3: morphCart is deleted. It was called from nowhere and referenced
        // two identifiers that do not exist in this file's scope -
        // buildSectionSelector and morph - so it would have thrown on its first
        // invocation. Morphing belongs to the controller's pipeline now.

        /**
         * Handles the reorder selling plans event.
         * @param {Event} event
         */
        handleReorderSellingPlans(event) {
          if (!event.detail?.sellingPlans) return;
          this.reorderSellingPlans(event.detail?.sellingPlans);
        },

        /**
         * Reorders the selling plans in the cart drawer.
         * @param {Array<number>} sellingPlansIdsArray
         */
        reorderSellingPlans(sellingPlansIdsArray = []) {
          // If we have ids, we need to follow the order of the ids, if there is not id in the array we need to add it to the end
          if (!Array.isArray(sellingPlansIdsArray) || sellingPlansIdsArray.length === 0) return;
        
          this.sellingPlans = [...this.sellingPlans].sort((a, b) => {
            const indexA = sellingPlansIdsArray.indexOf(a.id);
            const indexB = sellingPlansIdsArray.indexOf(b.id);
        
            // If both are not in idsArray, maintain original order
            if (indexA === -1 && indexB === -1) return 0;
            // If a is not in idsArray, move it to the end
            if (indexA === -1) return 1;
            // If b is not in idsArray, move it to the end
            if (indexB === -1) return -1;
            // Both are in idsArray, sort by their index
            return indexA - indexB;
          });

          // R57b-5: only the store owner may act on the global store or
          // rebuild the cart; both instances still reorder their own list.
          if (this._isStoreOwner && this.$store.cartDrawerStore.isSubscriptionActive && this.sellingPlan !== null) {
            // Codex round 6, P2: same omission as handleSyncSubscription. Setting
            // sellingPlan alone is no longer an instruction to anybody -
            // resolveTargetPlan stopped reading it in R57c-8 - so the requested
            // default-plan change was quietly discarded.
            const target = this.getDefaultSellingPlan() || null;
            this.$store.cartDrawerStore.desiredPlan = target;
            this.$store.cartDrawerStore.conversionGeneration += 1;
            this.$store.cartDrawerStore.setSellingPlan(target);

            if (this.cartProducts.length > 0) {
              this.updateCartItems();
            }
          }
        },

        /**
         * Format price in currency
         * @param {number} amount - Amount in cents
         * @returns {string}
         */
        formatMoney(amount) {
          if (amount == null || isNaN(amount)) return "£0.00";

          // Convert cents to pounds
          const value = amount / 100;

          return new Intl.NumberFormat("en-GB", {
            style: "currency",
            currency: "GBP",
            minimumFractionDigits: 2,
          }).format(value);
        }
    };
  });
});
